import { useState, useCallback, useEffect } from 'react';
import { Step, CallBackProps, STATUS, EVENTS, ACTIONS } from 'react-joyride';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

const TUTORIAL_COMPLETED_KEY = 'beautybook_tutorial_completed';
const TUTORIAL_SKIPPED_KEY = 'beautybook_tutorial_skipped';

export const tutorialSteps: Step[] = [
  {
    target: 'body',
    content: '¡Bienvenido a BeautyBook! Te guiaremos por las funciones principales de tu plataforma.',
    placement: 'center',
    disableBeacon: true,
    title: '¡Bienvenido! 👋',
  },
  {
    target: '[data-tutorial="dashboard"]',
    content: 'Este es tu panel principal. Aquí verás un resumen de tus citas del día, ingresos y estadísticas importantes.',
    title: 'Dashboard 📊',
    disableBeacon: true,
  },
  {
    target: '[data-tutorial="sales"]',
    content: 'Gestiona ventas directas desde tu negocio con el punto de venta integrado. Cobra productos y servicios al instante.',
    title: 'Ventas 🛒',
    disableBeacon: true,
  },
  {
    target: '[data-tutorial="analytics"]',
    content: 'Analiza el rendimiento de tu negocio con gráficos de ingresos, citas, retención de clientes y más.',
    title: 'Analytics 📈',
    disableBeacon: true,
  },
  {
    target: '[data-tutorial="marketing"]',
    content: 'Crea campañas de marketing para atraer nuevos clientes y fidelizar a los existentes.',
    title: 'Marketing 📣',
    disableBeacon: true,
  },
  {
    target: '[data-tutorial="ai-campaigns"]',
    content: 'Deja que la inteligencia artificial diseñe campañas personalizadas para tu negocio automáticamente.',
    title: 'Campañas IA 🚀',
    disableBeacon: true,
  },
  {
    target: '[data-tutorial="calendar"]',
    content: 'Aquí ves y gestionas todas tus citas. Puedes crear, modificar y cancelar reservas fácilmente con un solo clic.',
    title: 'Calendario 📅',
    disableBeacon: true,
  },
  {
    target: '[data-tutorial="services"]',
    content: 'Configura los servicios que ofreces con precios, duración y descripción. Tus clientes podrán reservarlos online.',
    title: 'Servicios 💅',
    disableBeacon: true,
  },
  {
    target: '[data-tutorial="portfolio"]',
    content: 'Sube fotos de tus trabajos para mostrar tu talento. Los clientes podrán ver tu portfolio antes de reservar.',
    title: 'Portfolio 🖼️',
    disableBeacon: true,
  },
  {
    target: '[data-tutorial="workers"]',
    content: 'Añade a los miembros de tu equipo y gestiona sus horarios de disponibilidad.',
    title: 'Equipo 👩‍💼',
    disableBeacon: true,
  },
  {
    target: '[data-tutorial="time-clock"]',
    content: 'Controla los fichajes de entrada y salida de tu equipo con el reloj de fichar digital.',
    title: 'Control Horario ⏱️',
    disableBeacon: true,
  },
  {
    target: '[data-tutorial="clients"]',
    content: 'Gestiona tu base de datos de clientes. Accede al historial de citas, preferencias y datos de contacto.',
    title: 'Clientes 👥',
    disableBeacon: true,
  },
  {
    target: '[data-tutorial="inventory"]',
    content: 'Controla el stock de tus productos. Recibe alertas cuando los niveles sean bajos.',
    title: 'Inventario 📦',
    disableBeacon: true,
  },
  {
    target: '[data-tutorial="suppliers"]',
    content: 'Gestiona tus proveedores de producto, realiza pedidos de compra y controla el inventario de forma centralizada.',
    title: 'Proveedores 🚚',
    disableBeacon: true,
  },
  {
    target: '[data-tutorial="payments"]',
    content: 'Historial de cobros, facturas simplificadas y resumen mensual que puedes enviar a tu gestor.',
    title: 'Caja 💰',
    disableBeacon: true,
  },
  {
    target: '[data-tutorial="invoices"]',
    content: 'Genera y gestiona facturas para tus clientes. Envíalas por email directamente desde la plataforma.',
    title: 'Facturas 📄',
    disableBeacon: true,
  },
  {
    target: '[data-tutorial="ai-agents"]',
    content: 'Configura agentes de inteligencia artificial que gestionan reservas y responden consultas automáticamente.',
    title: 'Agentes IA 🤖',
    disableBeacon: true,
  },
  {
    target: '[data-tutorial="candidate-messages"]',
    content: 'Revisa mensajes de candidatos de la bolsa de empleo y gestiona las conversaciones con potenciales empleados.',
    title: 'Mensajes 💬',
    disableBeacon: true,
  },
  {
    target: '[data-tutorial="settings"]',
    content: 'Personaliza tu negocio: horarios de apertura, notificaciones automáticas, métodos de pago y mucho más.',
    title: 'Configuración ⚙️',
    disableBeacon: true,
  },
  {
    target: '[data-tutorial="subscription"]',
    content: 'Gestiona tu plan de suscripción, revisa la facturación y descubre las funciones premium disponibles.',
    title: 'Mi Suscripción 💳',
    disableBeacon: true,
  },
  {
    target: '[data-tutorial="support"]',
    content: 'Necesitas ayuda? Contacta con nuestro equipo de soporte o consulta las preguntas frecuentes.',
    title: 'Soporte ❓',
    disableBeacon: true,
  },
  {
    target: 'body',
    content: '¡Listo! Ya conoces todas las funciones de tu plataforma. Explora cada sección para sacarle el máximo partido. Si necesitas ayuda, visita la sección de Soporte.',
    placement: 'center',
    disableBeacon: true,
    title: '¡Todo listo! 🎉',
  },
];

interface UseTutorialReturn {
  isRunning: boolean;
  stepIndex: number;
  steps: Step[];
  startTutorial: () => void;
  endTutorial: () => void;
  resetTutorial: () => void;
  handleCallback: (data: CallBackProps) => void;
  shouldShowTutorial: boolean;
  isCompleted: boolean;
  isSkipped: boolean;
}

export function useTutorial(): UseTutorialReturn {
  const { user } = useAuth();
  const [isRunning, setIsRunning] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isSkipped, setIsSkipped] = useState(false);
  const [shouldShowTutorial, setShouldShowTutorial] = useState(false);

  // Verificar si el usuario ya completó el tutorial (BD + localStorage)
  useEffect(() => {
    if (user?.id) {
      const checkTutorial = async () => {
        const { data } = await supabase
          .from('provider_settings')
          .select('tutorial_completed')
          .eq('provider_id', user.id)
          .maybeSingle();

        const completedInDB = data?.tutorial_completed === true;

        const completedKey = `${TUTORIAL_COMPLETED_KEY}_${user.id}`;
        const skippedKey = `${TUTORIAL_SKIPPED_KEY}_${user.id}`;
        const completedLocal = localStorage.getItem(completedKey) === 'true';
        const skipped = localStorage.getItem(skippedKey) === 'true';

        const completed = completedInDB || completedLocal;
        setIsCompleted(completed);
        setIsSkipped(skipped);
        setShouldShowTutorial(!completed && !skipped);
      };
      checkTutorial();
    }
  }, [user?.id]);

  const startTutorial = useCallback(() => {
    setStepIndex(0);
    setIsRunning(true);
    logger.info('[Tutorial] Starting tutorial');
  }, []);

  const endTutorial = useCallback(() => {
    setIsRunning(false);
    setStepIndex(0);
    if (user?.id) {
      const completedKey = `${TUTORIAL_COMPLETED_KEY}_${user.id}`;
      localStorage.setItem(completedKey, 'true');
      setIsCompleted(true);
      setShouldShowTutorial(false);
      supabase
        .from('provider_settings')
        .update({ tutorial_completed: true })
        .eq('provider_id', user.id)
        .then(() => logger.info('[Tutorial] Tutorial completed saved to DB'));
      logger.info('[Tutorial] Tutorial completed');
    }
  }, [user?.id]);

  const skipTutorial = useCallback(() => {
    setIsRunning(false);
    setStepIndex(0);
    if (user?.id) {
      const skippedKey = `${TUTORIAL_SKIPPED_KEY}_${user.id}`;
      localStorage.setItem(skippedKey, 'true');
      setIsSkipped(true);
      setShouldShowTutorial(false);
      supabase
        .from('provider_settings')
        .update({ tutorial_completed: true })
        .eq('provider_id', user.id)
        .then(() => logger.info('[Tutorial] Tutorial skipped saved to DB'));
      logger.info('[Tutorial] Tutorial skipped');
    }
  }, [user?.id]);

  const resetTutorial = useCallback(() => {
    if (user?.id) {
      const completedKey = `${TUTORIAL_COMPLETED_KEY}_${user.id}`;
      const skippedKey = `${TUTORIAL_SKIPPED_KEY}_${user.id}`;
      localStorage.removeItem(completedKey);
      localStorage.removeItem(skippedKey);
      setIsCompleted(false);
      setIsSkipped(false);
      setShouldShowTutorial(true);
      supabase
        .from('provider_settings')
        .update({ tutorial_completed: false })
        .eq('provider_id', user.id)
        .then(() => logger.info('[Tutorial] Tutorial reset in DB'));
      logger.info('[Tutorial] Tutorial reset');
    }
  }, [user?.id]);

  const handleCallback = useCallback((data: CallBackProps) => {
    const { status, type, action, index } = data;
    logger.info('[Tutorial] Callback:', { status, type, action, index });
    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);
      setStepIndex(nextIndex);
    }
    if (status === STATUS.FINISHED) {
      endTutorial();
    }
    if (status === STATUS.SKIPPED) {
      skipTutorial();
    }
  }, [endTutorial, skipTutorial]);

  return {
    isRunning,
    stepIndex,
    steps: tutorialSteps,
    startTutorial,
    endTutorial,
    resetTutorial,
    handleCallback,
    shouldShowTutorial,
    isCompleted,
    isSkipped,
  };
}

export default useTutorial;